import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { ArrowLeft, Mail, Users, FileText, Play, Pause, Pencil, Trash2, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEND_INTERVAL_PRESETS = [
    { value: 'immediate', label: 'Immediate (0 sec)', min: 0, max: 0, warning: true },
    { value: 'fast', label: 'Fast (30–90 sec)', min: 30, max: 90 },
    { value: 'standard', label: 'Standard (1–5 min)', min: 60, max: 300 },
    { value: 'conservative', label: 'Conservative (2–10 min)', min: 120, max: 600 },
    { value: 'custom', label: 'Custom' }
];

const getPresetFromConfig = (config) => {
    const min = config?.min_delay_seconds ?? null;
    const max = config?.max_delay_seconds ?? null;
    if (min == null && max == null) return 'standard';
    const preset = SEND_INTERVAL_PRESETS.find(p => p.value !== 'custom' && p.min === min && p.max === max);
    return preset ? preset.value : 'custom';
};

const STATUS_COLORS = {
    Draft: 'bg-slate-100 text-slate-700',
    Scheduled: 'bg-amber-100 text-amber-800',
    Running: 'bg-emerald-100 text-emerald-800',
    Paused: 'bg-orange-100 text-orange-800',
    Completed: 'bg-slate-100 text-slate-600'
};

const CampaignDetailPage = () => {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [enrollments, setEnrollments] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [diagnostics, setDiagnostics] = useState(null);
    const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);

    useEffect(() => {
        if (campaignId) {
            fetchCampaign();
            fetchEnrollments();
            fetchAccounts();
        }
    }, [campaignId]);

    const fetchCampaign = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL}/campaigns/${campaignId}`);
            setCampaign(data);
            const cfg = data.config || { daily_limit: 50, start_time: '09:00', end_time: '17:00', timezone: 'America/New_York', ignore_business_hours: false };
            const configWithDelay = { ...cfg, min_delay_seconds: cfg.min_delay_seconds ?? 60, max_delay_seconds: cfg.max_delay_seconds ?? 300 };
            setEditForm({
                name: data.name,
                subject: data.subject || '',
                body_html: data.body_html || '',
                bounce_threshold: data.bounce_threshold ?? 3,
                sender_account_ids: data.sender_account_ids || [],
                config: configWithDelay,
                sendIntervalPreset: getPresetFromConfig(configWithDelay)
            });
        } catch (e) {
            if (e.response?.status === 404) {
                toast.error('Campaign not found');
                navigate('/campaigns');
            } else {
                toast.error('Failed to load campaign');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchEnrollments = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/campaigns/${campaignId}/leads`);
            setEnrollments(data);
        } catch (e) {
            console.error('Failed to fetch enrollments', e);
        }
    };

    const fetchAccounts = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/accounts`);
            setAccounts(data);
        } catch (e) {
            console.error('Failed to fetch accounts', e);
        }
    };

    const fetchDiagnostics = async () => {
        setDiagnosticsLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/campaigns/${campaignId}/send-diagnostics`);
            setDiagnostics(data);
        } catch (e) {
            toast.error('Failed to load diagnostics');
        } finally {
            setDiagnosticsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!editForm) return;
        setSaving(true);
        try {
            const { sendIntervalPreset, ...payload } = editForm;
            await axios.patch(`${API_URL}/campaigns/${campaignId}`, payload);
            toast.success('Campaign updated');
            setEditOpen(false);
            fetchCampaign();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to update'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/campaigns/${campaignId}`);
            setDeleteOpen(false);
            toast.success('Campaign deleted');
            navigate('/campaigns');
        } catch (e) {
            toast.error(getApiError(e, 'Failed to delete'));
        } finally {
            setDeleting(false);
        }
    };

    const handleStart = async () => {
        try {
            await axios.post(`${API_URL}/campaigns/${campaignId}/start`);
            toast.success('Campaign started');
            fetchCampaign();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to start'));
        }
    };

    const handlePause = async () => {
        try {
            await axios.post(`${API_URL}/campaigns/${campaignId}/pause`);
            toast.success('Campaign paused');
            fetchCampaign();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to pause'));
        }
    };

    const handleResume = async () => {
        try {
            await axios.post(`${API_URL}/campaigns/${campaignId}/resume`);
            toast.success('Campaign resumed');
            fetchCampaign();
        } catch (e) {
            toast.error(getApiError(e, 'Failed to resume'));
        }
    };

    const getProgress = () => {
        const sent = campaign?.stats?.sent || 0;
        const total = enrollments.length || 1;
        return Math.min(100, (sent / total) * 100);
    };

    if (loading && !campaign) {
        return (
            <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!campaign) return null;

    return (
        <div className="p-6 md:p-8 lg:p-10 space-y-8">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')} className="rounded-lg">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Campaigns
                </Button>
            </div>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-bold text-slate-900">{campaign.name}</h1>
                        <Badge className={STATUS_COLORS[campaign.status]}>{campaign.status}</Badge>
                    </div>
                    <p className="text-sm text-slate-500">
                        Sent: {campaign.stats?.sent || 0} | Opened: {campaign.stats?.opened || 0} | Replied: {campaign.stats?.replied || 0} | Bounced: {campaign.stats?.bounced || 0}
                    </p>
                    {(campaign.last_updated_by_email || campaign.last_updated_at) && (
                        <p className="text-xs text-slate-400 mt-1">
                            Last updated{campaign.last_updated_by_email ? ` by ${campaign.last_updated_by_email}` : ''}{campaign.last_updated_at ? ` at ${new Date(campaign.last_updated_at).toLocaleString()}` : ''}
                        </p>
                    )}
                    <div className="mt-2 w-64">
                        <Progress value={getProgress()} className="h-2" />
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {campaign.status === 'Draft' && (
                        <Button size="sm" onClick={handleStart} className="rounded-lg">
                            <Play className="h-4 w-4 mr-2" /> Start
                        </Button>
                    )}
                    {campaign.status === 'Running' && (
                        <Button size="sm" variant="outline" onClick={handlePause} className="rounded-lg">
                            <Pause className="h-4 w-4 mr-2" /> Pause
                        </Button>
                    )}
                    {campaign.status === 'Paused' && (
                        <Button size="sm" onClick={handleResume} className="rounded-lg">
                            <Play className="h-4 w-4 mr-2" /> Resume
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="rounded-lg">
                        <Pencil className="h-4 w-4 mr-2" /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => setDeleteOpen(true)} className="rounded-lg">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <Card className="p-6 rounded-xl border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Mail className="h-5 w-5" /> Configuration
                    </h2>
                    <div className="space-y-3 text-sm">
                        <div>
                            <span className="text-slate-500">Daily limit:</span> {campaign.config?.daily_limit ?? 50}
                        </div>
                        <div>
                            <span className="text-slate-500">Gap between emails:</span>{' '}
                            {(campaign.config?.min_delay_seconds ?? 60) === 0 && (campaign.config?.max_delay_seconds ?? 300) === 0
                                ? 'Immediate (0 sec)'
                                : `${campaign.config?.min_delay_seconds ?? 60}–${campaign.config?.max_delay_seconds ?? 300} sec`}
                        </div>
                        <div>
                            <span className="text-slate-500">Hours:</span> {campaign.config?.ignore_business_hours ? '24/7 (ignored)' : `${campaign.config?.start_time ?? '09:00'} - ${campaign.config?.end_time ?? '17:00'} (${campaign.config?.timezone ?? 'America/New_York'})`}
                        </div>
                        {(campaign.status === 'Running' || campaign.status === 'Paused') && (
                            <div className="mt-2">
                                <Button size="sm" variant="outline" onClick={fetchDiagnostics} disabled={diagnosticsLoading} className="rounded-lg">
                                    {diagnosticsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertCircle className="h-4 w-4 mr-2" />}
                                    Why no emails?
                                </Button>
                            </div>
                        )}
                        <div>
                            <span className="text-slate-500">Bounce threshold:</span> {campaign.bounce_threshold ?? 3}%
                        </div>
                        <div>
                            <span className="text-slate-500">Sender accounts:</span>{' '}
                            {campaign.sender_account_ids?.length || 0}
                            {accounts.length > 0 && (
                                <span className="text-slate-500 ml-1">
                                    ({campaign.sender_account_ids?.map(id => accounts.find(a => a.id === id)?.email).filter(Boolean).join(', ') || '—'})
                                </span>
                            )}
                        </div>
                    </div>
                </Card>

                <Card className="p-6 rounded-xl border-slate-200">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <FileText className="h-5 w-5" /> Content
                    </h2>
                    <div className="space-y-3 text-sm">
                        <div>
                            <span className="text-slate-500 font-medium">Subject:</span>
                            <p className="mt-1 text-slate-900">{campaign.subject || '—'}</p>
                        </div>
                        <div>
                            <span className="text-slate-500 font-medium">Body:</span>
                            <div
                                className="mt-1 text-slate-900 [&_p]:my-1"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(campaign.body_html || '<p class="text-slate-400">—</p>')
                                }}
                            />
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="p-6 rounded-xl border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5" /> Enrolled Leads ({enrollments.length})
                </h2>
                <ScrollArea className="h-[300px] rounded-lg border">
                    <div className="p-2 space-y-1">
                        {enrollments.length === 0 ? (
                            <p className="text-sm text-slate-500 py-4 text-center">No leads enrolled</p>
                        ) : (
                            enrollments.map((e) => (
                                <div
                                    key={e.id || e.lead_id}
                                    className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-slate-50"
                                >
                                    <div>
                                        <p className="font-medium text-slate-900">{e.lead?.name || '—'}</p>
                                        <p className="text-xs text-slate-500">{e.lead?.email || e.lead_id}</p>
                                    </div>
                                    <Badge variant={e.status === 'sent' ? 'default' : e.status === 'pending' ? 'secondary' : 'outline'}>
                                        {e.status}
                                    </Badge>
                                </div>
                            ))
                        )}
                    </div>
                </ScrollArea>
            </Card>

            {diagnostics && (
                <Dialog open={!!diagnostics} onOpenChange={(open) => !open && setDiagnostics(null)}>
                    <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                            <DialogTitle>Send Diagnostics</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 text-sm">
                            <div className="flex items-center gap-2">
                                <span className={diagnostics.can_send ? 'text-emerald-600 font-medium' : 'text-amber-600 font-medium'}>
                                    {diagnostics.can_send ? '✓ Ready to send' : '✗ Blocked'}
                                </span>
                            </div>
                            {diagnostics.blocking_reasons?.length > 0 && (
                                <div className="rounded-lg bg-amber-50 p-3 space-y-1">
                                    <p className="font-medium text-amber-800">Blocking reasons:</p>
                                    <ul className="list-disc list-inside text-amber-700 space-y-1">
                                        {diagnostics.blocking_reasons.map((r, i) => (
                                            <li key={i}>{r}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="space-y-1 text-slate-600">
                                <p><span className="font-medium">Status:</span> {diagnostics.status}</p>
                                <p><span className="font-medium">Business hours:</span> {diagnostics.business_hours}</p>
                                <p><span className="font-medium">Current time (TZ):</span> {diagnostics.current_time_in_tz}</p>
                                <p><span className="font-medium">Pending enrollments:</span> {diagnostics.pending_enrollments}</p>
                                <p><span className="font-medium">Available sender:</span> {diagnostics.available_sender ? 'Yes' : 'No'}</p>
                            </div>
                            <p className="text-xs text-slate-500 italic">{diagnostics.note}</p>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit Campaign</DialogTitle>
                    </DialogHeader>
                    {editForm && (
                        <div className="space-y-4 py-4">
                            <div>
                                <Label>Name</Label>
                                <Input
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    className="rounded-lg mt-1"
                                />
                            </div>
                            <div>
                                <Label>Subject</Label>
                                <Input
                                    value={editForm.subject}
                                    onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                                    className="rounded-lg mt-1"
                                />
                            </div>
                            <div>
                                <Label>Body</Label>
                                <div className="mt-1 border rounded-lg p-2 min-h-[150px]">
                                    <RichTextEditor
                                        content={editForm.body_html}
                                        onChange={(html) => setEditForm({ ...editForm, body_html: html })}
                                        showVariablePicker={true}
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="ignore-hours"
                                    checked={editForm.config?.ignore_business_hours ?? false}
                                    onCheckedChange={(checked) => setEditForm({
                                        ...editForm,
                                        config: { ...editForm.config, ignore_business_hours: !!checked }
                                    })}
                                />
                                <Label htmlFor="ignore-hours" className="font-normal">Send 24/7 (ignore business hours)</Label>
                            </div>
                            {!editForm.config?.ignore_business_hours && (
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <Label>Start time</Label>
                                        <Input
                                            type="time"
                                            value={editForm.config?.start_time ?? '09:00'}
                                            onChange={(e) => setEditForm({
                                                ...editForm,
                                                config: { ...editForm.config, start_time: e.target.value || '09:00' }
                                            })}
                                            className="rounded-lg mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label>End time</Label>
                                        <Input
                                            type="time"
                                            value={editForm.config?.end_time ?? '17:00'}
                                            onChange={(e) => setEditForm({
                                                ...editForm,
                                                config: { ...editForm.config, end_time: e.target.value || '17:00' }
                                            })}
                                            className="rounded-lg mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label>Timezone</Label>
                                        <Select
                                            value={editForm.config?.timezone ?? 'America/New_York'}
                                            onValueChange={(v) => setEditForm({
                                                ...editForm,
                                                config: { ...editForm.config, timezone: v }
                                            })}
                                        >
                                            <SelectTrigger className="rounded-lg mt-1">
                                                <SelectValue placeholder="Timezone" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                                                <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                                                <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                                                <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                                                <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST)</SelectItem>
                                                <SelectItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectItem>
                                                <SelectItem value="Australia/Sydney">Australia/Sydney (AEST)</SelectItem>
                                                <SelectItem value="UTC">UTC</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Daily Limit</Label>
                                    <Input
                                        type="number"
                                        value={editForm.config?.daily_limit ?? 50}
                                        onChange={(e) => setEditForm({
                                            ...editForm,
                                            config: { ...editForm.config, daily_limit: parseInt(e.target.value) || 50 }
                                        })}
                                        className="rounded-lg mt-1"
                                    />
                                </div>
                                <div>
                                    <Label>Bounce Threshold (%)</Label>
                                    <Input
                                        type="number"
                                        value={editForm.bounce_threshold}
                                        onChange={(e) => setEditForm({ ...editForm, bounce_threshold: parseInt(e.target.value) || 3 })}
                                        className="rounded-lg mt-1"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Gap between emails</Label>
                                <p className="text-xs text-slate-500 mt-0.5 mb-2">Delay between each email sent. Sending too frequently can land in spam.</p>
                                <Select
                                    value={editForm.sendIntervalPreset ?? getPresetFromConfig(editForm.config)}
                                    onValueChange={(v) => {
                                        const preset = SEND_INTERVAL_PRESETS.find(p => p.value === v);
                                        if (preset && preset.value !== 'custom') {
                                            setEditForm({
                                                ...editForm,
                                                sendIntervalPreset: preset.value,
                                                config: { ...editForm.config, min_delay_seconds: preset.min, max_delay_seconds: preset.max }
                                            });
                                        } else {
                                            const min = editForm.config?.min_delay_seconds ?? 90;
                                            const max = editForm.config?.max_delay_seconds ?? 180;
                                            setEditForm({
                                                ...editForm,
                                                sendIntervalPreset: 'custom',
                                                config: { ...editForm.config, min_delay_seconds: min, max_delay_seconds: max }
                                            });
                                        }
                                    }}
                                >
                                    <SelectTrigger className="rounded-lg mt-1">
                                        <SelectValue placeholder="Select interval" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SEND_INTERVAL_PRESETS.map((p) => (
                                            <SelectItem key={p.value} value={p.value}>
                                                {p.label}{p.warning ? ' ⚠️' : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {(editForm.sendIntervalPreset ?? getPresetFromConfig(editForm.config)) === 'immediate' && (
                                    <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                                        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                                        <p>Emails sent too frequently can trigger spam filters and hurt deliverability. Consider using Standard or Conservative for better inbox placement.</p>
                                    </div>
                                )}
                                {(editForm.sendIntervalPreset ?? getPresetFromConfig(editForm.config)) === 'custom' && (
                                    <div className="grid grid-cols-2 gap-4 mt-2">
                                        <div>
                                            <Label>Minimum delay (seconds)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                placeholder="e.g. 60"
                                                value={editForm.config?.min_delay_seconds ?? ''}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    config: { ...editForm.config, min_delay_seconds: Math.max(0, parseInt(e.target.value) || 0) }
                                                })}
                                                className="rounded-lg mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label>Maximum delay (seconds)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                placeholder="e.g. 300"
                                                value={editForm.config?.max_delay_seconds ?? ''}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    config: { ...editForm.config, max_delay_seconds: Math.max(0, parseInt(e.target.value) || 0) }
                                                })}
                                                className="rounded-lg mt-1"
                                            />
                                        </div>
                                        <p className="text-xs text-slate-500 col-span-2">Enter min and max seconds between each email. A random delay in this range is used.</p>
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label>Sender Accounts</Label>
                                <div className="space-y-2 mt-1 max-h-40 overflow-y-auto border rounded-lg p-2">
                                    {accounts.filter(a => a.is_active).map((acc) => (
                                        <div key={acc.id} className="flex items-center gap-2">
                                            <Checkbox
                                                checked={editForm.sender_account_ids?.includes(acc.id)}
                                                onCheckedChange={(checked) => {
                                                    const ids = editForm.sender_account_ids || [];
                                                    setEditForm({
                                                        ...editForm,
                                                        sender_account_ids: checked ? [...ids, acc.id] : ids.filter(id => id !== acc.id)
                                                    });
                                                }}
                                            />
                                            <span className="text-sm">{acc.email}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                                <Button onClick={handleSave} disabled={saving}>
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Save Changes
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete &quot;{campaign.name}&quot;? This will remove the campaign and all enrollment records. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
                            {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default CampaignDetailPage;
